<template>
  <div class="row q-col-gutter-sm">
    <div
      v-for="(bed, index) in unitTypeMetric"
      :key="bed.bed"
      class="col"
      :class="{
        'col-3': unitTypeMetric.length < 3,
        'col-6': $q.screen.lt.lg,
        'col-12': $q.screen.lt.md
      }"
    >
      <q-card :id="`intercom-${index}-unit-type-metric`" flat bordered>
        <q-card-section
          class="row justify-between items-center"
          :style="{
            borderBottom: `4px solid ${unitTypeColor(bed.bed)}`
          }"
        >
          <div class="text-h6">{{ unitTypeName(bed.bed) }}</div>
          <div class="trailing-text">{{ trailingText }}</div>
        </q-card-section>
        <q-card-section class="row q-col-gutter-x-md q-col-gutter-y-xs">
          <div
            v-for="ins in bed.insights"
            :key="ins.label"
            class="text-caption col-12 justify-between flex"
            :class="{ 'col-6': $q.screen.lt.md }"
          >
            <span class="text-grey-7">{{ ins.label }} </span>

            <span class="highlighted-number">{{ ins.valueFormatted }}</span>
          </div>
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<script lang="ts">
import type { Availability, PropertyDetailsResponse, Price, AvailabilityPeriod } from 'common'
import { daysOnMarket, avgPsf } from 'common'
import type { PropType } from 'vue'
import { formatMoney, formatNumber, avg, sum, unitTypeColor } from '@/utils'
import { differenceInDays } from 'date-fns'

type Insight = {
  bed: number
  insights: {
    label: string
    description: string
    valueFormatted: string
  }[]
}

export default {
  name: 'PropertyCompareUnitTypeMetrics',
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    }
  },
  data() {
    return {
      unitTypeColor,
      formatMoney,
      formatNumber,
      metrics: [
        {
          label: '# Listings',
          description: 'Number of units of this type at your property',
          getter: () => [1],
          agg: sum,
          formatter: (val: number | null) => (val ? formatNumber(val) : '−')
        },
        {
          label: 'Days on Market',
          description: 'Average number of days a unit is on the market before being leased',
          getter: ({ period }) => [daysOnMarket(period, this.dateRange.to)],
          agg: avg,
          formatter: (val: number | null) => (val ? formatNumber(val) : '−')
        },
        {
          label: 'Asking Rent',
          description: 'Average rent for a unit, before applying detected concessions',
          getter: ({ lastHistory }) => [lastHistory.price ?? lastHistory.min_price],
          agg: avg,
          formatter: (val: number | null) => (val ? formatMoney(val, 0) : '−')
        },
        {
          label: 'Asking Rent PSF',
          description:
            'Average asking rent per square foot for a unit, before applying concessions',
          getter: ({ unit, lastHistory }) => [
            {
              sqft: unit.sqft || unit.min_sqft,
              price: lastHistory.price ?? lastHistory.min_price
            }
          ],
          agg: avgPsf,
          formatter: (val: number | null) => (val ? formatMoney(val, 2) : '−')
        },
        {
          label: 'Effective Rent',
          description: 'Average effective rent for a unit, after applying detected concessions',
          getter: ({ lastHistory }) => [
            lastHistory.effective_price || lastHistory.min_effective_price
          ],
          agg: avg,
          formatter: (val: number | null) => (val ? formatMoney(val, 0) : '−')
        },
        {
          label: 'Effective Rent PSF',
          description:
            'Average effective rent per square foot for a unit, after applying detected concessions',
          getter: ({ unit, lastHistory }) => [
            {
              sqft: unit.sqft || unit.min_sqft,
              price: lastHistory.effective_price || lastHistory.min_effective_price
            }
          ],
          agg: avgPsf,
          formatter: (val: number | null) => (val ? formatMoney(val, 2) : '−')
        }
      ] as {
        label: string
        description: string
        getter: (val: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }) => number[]
        agg: (vals: (number | null | { price: number[]; sqft: number[] })[]) => number | null
        formatter: (val: number | null) => string
      }[]
    }
  },
  computed: {
    trailingText() {
      if (this.dateRange.to.toDateString() !== new Date().toDateString()) {
        return 'Custom date range'
      }
      const days = differenceInDays(this.dateRange.to, this.dateRange.from)
      if (days < 7) {
        return `Trailing ${days} day${days > 1 ? 's' : ''}`
      }
      if (days % 30 === 0) {
        const months = days / 30
        return `Trailing ${months} month${months > 1 ? 's' : ''}`
      }
      if (days % 7 === 0) {
        const weeks = days / 7
        return `Trailing ${weeks} week${weeks > 1 ? 's' : ''}`
      }
      return `trailing ${days} day${days > 1 ? 's' : ''}`
    },
    unitTypeMetric(): Insight[] {
      const beds = [0, 1, 2, 3, 4, 5]
      const metricValues: Record<number, Record<string, { values: (null | number)[] }>> = {}

      // instanciate all of them
      for (const bed of beds) {
        metricValues[bed] = {}
        for (const metric of this.metrics) {
          metricValues[bed][metric.label] = { values: [] }
        }
      }

      for (const { units } of this.filteredProperties) {
        for (const { unit, lastHistory, period } of units) {
          const bed = unit.bed
          for (const metric of this.metrics) {
            if (bed !== null && beds.includes(bed)) {
              const val = metric.getter({ unit, lastHistory, period })
              metricValues[bed][metric.label].values.push(...val)
            }
          }
        }
      }

      return beds
        .filter((bed) => metricValues[bed]['# Listings'].values.length > 0)
        .map((bed) => {
          return {
            bed,
            insights: this.metrics.map((metric) => {
              const values = metricValues[bed][metric.label].values
              return {
                label: metric.label,
                description: metric.description,
                valueFormatted: metric.formatter(metric.agg(values))
              }
            })
          }
        })
    }
  },
  methods: {
    unitTypeName(bed: number): string {
      return bed === 0 ? 'Studio' : `${bed} Beds`
    }
  }
}
</script>

<style scoped>
.trailing-text {
  font-size: 1em;
  color: #999;
  font-weight: 500;
}

.highlighted-number {
  font-weight: 600;
}
</style>
